import { useEffect, useState, type JSX } from 'react'
import { useAuth } from '@/stores/auth'
import { Register } from '@/routes/auth/Register'
import { Login } from '@/routes/auth/Login'
import { Verify } from '@/routes/auth/Verify'
import { Recover } from '@/routes/auth/Recover'
import { Reset } from '@/routes/auth/Reset'
import { PinLogin } from '@/routes/auth/PinLogin'
import { PinSetup } from '@/routes/auth/PinSetup'
import { SecuritySettings } from '@/routes/settings/Security'
import { ComponentsShowcase } from '@/routes/dev/Components'
import { ApiDocs } from '@/routes/dev/ApiDocs'
import { NotFound } from '@/routes/error/NotFound'
import { ServerError } from '@/routes/error/ServerError'
import { Forbidden } from '@/routes/error/Forbidden'
import { Offline } from '@/routes/error/Offline'
import { Maintenance } from '@/routes/error/Maintenance'
import { OAuthCallback } from '@/routes/redirect/OAuthCallback'
import { Welcome } from '@/routes/onboarding/Welcome'
import { Survey } from '@/routes/onboarding/Survey'
import { Icon } from '@/components/icons/Icon'
import { Toaster } from '@/components/ui/Toaster'
import { CookieBannerProvider } from '@/components/consent/CookieBannerProvider'
import { CookieSettingsPage } from '@/routes/account/privacy/cookies'
import { PrivacyPolicyPage } from '@/legal/pages/PrivacyPolicyPage'
import { TermsOfUsePage } from '@/legal/pages/TermsOfUsePage'
import { CookiePolicyPage } from '@/legal/pages/CookiePolicyPage'
import { SubprocessorsPage } from '@/legal/pages/SubprocessorsPage'
import { LegalIndexPage } from '@/legal/pages/LegalIndexPage'

// Build trigger: 2026-05-08 — Yandex OAuth credentials added to GitHub Secrets
function resolveRoute(path: string): JSX.Element | null {
  // Auth
  if (path === '/auth/register') return <Register />
  if (path === '/auth/login') return <Login />
  if (path === '/auth/verify') return <Verify />
  if (path === '/auth/recover') return <Recover />
  if (path === '/auth/reset') return <Reset />
  if (path === '/auth/pin') return <PinLogin />
  if (path === '/auth/pin-setup') return <PinSetup />
  if (path === '/auth/oauth/callback') return <OAuthCallback />
  // Onboarding (US-E2-001)
  if (path === '/welcome') return <Welcome />
  if (path === '/onboarding/survey') return <Survey />
  // Settings primary route — под /app/* (попадает в Caddy @spa /app/* matcher).
  // /settings/security оставлен alias для backward compat (если Caddy расширят).
  if (path === '/app/settings/security') return <SecuritySettings />
  if (path === '/settings/security') return <SecuritySettings />
  if (path === '/account/privacy/cookies') return <CookieSettingsPage />
  // Legal
  if (path === '/legal') return <LegalIndexPage />
  if (path === '/legal/privacy') return <PrivacyPolicyPage />
  if (path === '/legal/terms') return <TermsOfUsePage />
  if (path === '/legal/cookie') return <CookiePolicyPage />
  if (path === '/legal/subprocessors') return <SubprocessorsPage />
  // Dev
  if (path === '/dev/components') return <ComponentsShowcase />
  if (path === '/dev/api') return <ApiDocs />
  // Error & status pages (могут открываться напрямую через URL)
  if (path === '/404') return <NotFound />
  if (path === '/500') return <ServerError />
  if (path === '/403') return <Forbidden />
  if (path === '/maintenance') return <Maintenance />
  if (path === '/offline') return <Offline />
  return null
}

const KNOWN_PATHS = new Set([
  '/', '/auth/register', '/auth/login', '/auth/verify', '/auth/recover',
  '/auth/reset', '/auth/pin', '/auth/pin-setup', '/auth/oauth/callback',
  '/welcome', '/onboarding/survey',
  '/app/settings/security', '/settings/security',
  '/account/privacy/cookies',
  '/legal', '/legal/privacy', '/legal/terms', '/legal/cookie', '/legal/subprocessors',
  '/dev/components', '/dev/api',
  '/404', '/500', '/403', '/maintenance', '/offline',
])

export function App() {
  const { isAuthed, user, logout, hydrate } = useAuth()
  useEffect(() => { hydrate() }, [hydrate])

  // Online/offline detection — full-screen Offline страница если связи нет.
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true)
  useEffect(() => {
    const sync = () => setOnline(navigator.onLine)
    window.addEventListener('online', sync)
    window.addEventListener('offline', sync)
    return () => {
      window.removeEventListener('online', sync)
      window.removeEventListener('offline', sync)
    }
  }, [])

  if (!online) return <><Offline /><Toaster /><CookieBannerProvider /></>

  const path = window.location.pathname
  const page = resolveRoute(path)

  let body: JSX.Element
  if (page) {
    body = page
  } else if (!KNOWN_PATHS.has(path) && path !== '/') {
    // Любой нераспознанный путь → 404
    body = <NotFound />
  } else if (isAuthed && user) {
    // NB: redirect новых юзеров на /welcome живёт в Login/PinLogin/OAuthCallback
    // (см. lib/authRedirect.ts). На UAT/prod `/` обслуживается лендингом
    // (www-v3/index.html), поэтому проверка welcomed_at в App.tsx не работала.
    body = (
      <div className="min-h-screen flex items-center justify-center bg-surface-canvas px-4">
        <div className="max-w-2xl text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-status-success-bg text-status-success-text rounded-full text-xs font-bold tracking-wider uppercase">
            <Icon name="check" size={14} />
            Вход выполнен
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold font-display text-text-primary">
            Привет, акционер 👋
          </h1>
          <p className="text-base lg:text-lg text-text-secondary">
            Вошёл как <span className="font-semibold text-text-primary">{user.email}</span>{' '}
            {user.email_verified ? '✅' : '⏳'}
            <br/>
            Холдинг-дашборд скоро будет здесь.
          </p>
          <button onClick={logout}
            className="px-6 py-3 rounded-lg bg-action-secondary-bg border border-action-secondary-border hover:bg-action-secondary-bg-hover text-text-primary font-semibold transition-colors">
            Выйти
          </button>
        </div>
      </div>
    )
  } else {
    body = (
      <div className="min-h-screen flex items-center justify-center bg-surface-canvas px-4">
        <div className="max-w-md text-center space-y-6">
          <h1 className="text-3xl font-bold font-display text-text-primary">Привет 👋</h1>
          <p className="text-text-secondary">Чтобы нанять AI CEO — войди или создай аккаунт.</p>
          <div className="flex gap-3 justify-center">
            <a href="/auth/register" className="px-5 py-2.5 rounded-lg bg-action-primary-bg text-text-on-brand font-semibold">Создать аккаунт</a>
            <a href="/auth/login" className="px-5 py-2.5 rounded-lg bg-action-secondary-bg border border-action-secondary-border text-text-primary font-semibold">Войти</a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {body}
      <Toaster />
      <CookieBannerProvider />
    </>
  )
}
